import "./css/App.css";
import { Fragment, useEffect, useLayoutEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import EventRoute from "./routes/EventRoute";
import ProfileRoute from "./routes/ProfileRoute";
import SubscriptionRoute from "./routes/SubscriptionRoute";
import ExploreRoute from "./routes/ExploreRoute";
import CommunityPage from "./screens/CommunityPage";
import LoadingScreen from "./screens/LoadingScreen";
import {
  LandingPageNavigationId,
  NavigationId,
} from "./navigation/AppParamList";
import { useDispatch, useSelector } from "react-redux";
import { accountActions, getAccountSlice } from "./redux/slices/accountSlice";
import { LightTheme, useTheme } from "./hooks/useTheme";
import HomeRoute from "./routes/HomeRoute";
import ScrollToTop from "./utils/ScrollToTop";
import ImportContacts from "./screens/Import/ImportContacts";
import { useInitialAppLoad } from "./hooks/useInitialAppLoad";
import EventbriteIntegration from "./screens/Integrations/EventbriteIntegration";
import CampaignDashboard from "./screens/Campaigns/CampaignDashboard";
import ConfirmActionPopup from "./components/Containers/ConfirmPopups/ConfirmActionPopup";
import AudienceListDashboard from "./screens/AudienceLists/AudienceListDashboard";
import DefaultLandingPage from "./screens/LandingPage/DefaultLandingPage";
import TextingProductPage from "./screens/LandingPage/TextingProductPage";
import MonetizationProductPage from "./screens/LandingPage/MonetizationProductPage";
import LoginWelcome from "./screens/Login/LoginWelcome";
import LoginOnboarding from "./screens/Login/LoginOnboarding";
import WelcomeLandingPage from "./screens/WelcomeLandingPage";
import PricingPage from "./screens/LandingPage/PricingPage";
import { MarkitPlusModal } from "./components/Subscription/MarkitPlusModal";
import { AccountMismatchModal } from "./components/DisplayModal/AccountMismatchModal";
import { UpdateWebAppBanner } from "./components/DisplayModal/UpdateWebAppBanner";
import OnboardingTourStepIndicator from "./components/Onboarding/OnboardingTourStepIndicator";
import SubscriptionPhoneOnboarding from "./screens/Subscription/SubscriptionPhoneOnboarding";
import StripeSuccessfulPage from "./screens/LandingPage/StripeSuccessfulPage";
import NewProductRoute from "./routes/NewProductRoute";
import PressMediaPage from "./screens/LandingPage/PressMediaPage";
import InstagramConnectionPage from "./screens/LandingPage/InstagramConnectionPage";
import FacebookConnectionPage from "./screens/LandingPage/FacebookConnectionPage";
import GoogleCalendarConnectionPage from "./screens/LandingPage/GoogleCalendarConnectionPage";
import DMAutomationDashboard from "./screens/AutoDM/DMAutomationDashboard";
import MarkitFundraisingScreen from "./internalTools/public/markit-fundraising/MarkitFundraisingScreen";
import MasterDashboardScreen from "./internalTools/admin/master-dashboard/MasterDashboardScreen";
import OAuthConsentScreen from "./screens/OAuth/OAuthConsentScreen";

/**
 * Documentation of the different webapp URLs
 * https://docs.google.com/document/d/1E_lDN3HcWl1hQFY-UujdHVw2VtRTXMEjihSoSBm9Mwg
 *
 */
const App = () => {
  const dispatch = useDispatch();
  const { currentRoleTicket, currentEventFormQuestions } =
    useSelector(getAccountSlice);
  const { pathname } = useLocation();
  // If we need to put app under maintenance
  const underMaintenance = false;
  const { theme } = useTheme();

  useInitialAppLoad();
  // 12/10/25: Commented out to reduce number of mixpanel events
  // useOnMount(() => {
  //   mixpanel.track("Viewed Webapp Link");
  // });

  // called on unmount when leaving sign in or onboarding route and clears the currentRoleTicket if it exists,
  // unless the user presses a certain button on the screen
  useEffect(() => {
    if (
      currentRoleTicket &&
      !pathname.includes(NavigationId.SIGN_IN) &&
      !pathname.includes(NavigationId.ONBOARDING)
    ) {
      // for clearing accepting event role data
      dispatch(accountActions.setCurrentRoleTicket(undefined));
    }
    if (
      currentEventFormQuestions.length > 0 &&
      !pathname.includes(NavigationId.NEW_PRODUCT) &&
      !pathname.includes("/edit") &&
      !pathname.includes("/editLink") &&
      !pathname.includes(NavigationId.WELCOME)
    ) {
      // for clearing the event selected saved form questions on event creation
      dispatch(accountActions.resetCurrentEventFormQuestions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // set the background color beyond the set window
  useLayoutEffect(() => {
    document.body.style.backgroundColor =
      theme === LightTheme ? "white" : "black";
  }, [theme, pathname]);

  // TODO (jasonx): turn this into something more typesafe
  if (underMaintenance) {
    return <LoadingScreen underMaintenance={underMaintenance} />;
  } else {
    return (
      <>
        <ScrollToTop>
          <Routes>
            <Route path={NavigationId.HOME} element={<HomeRoute />} />
            <Route
              path={NavigationId.SUBSCRIPTION_PLAN}
              element={<SubscriptionRoute />}
            />
            <Route
              path="secondary-onboarding"
              element={<SubscriptionPhoneOnboarding />}
            />
            <Route path="/e/:eventId/*" element={<EventRoute />} />
            <Route
              path="/a/:audienceListId"
              element={<AudienceListDashboard />}
            />
            <Route path="/c/:campaignId" element={<CampaignDashboard />} />
            <Route
              path="/dm/:dmAutomationId"
              element={<DMAutomationDashboard />}
            />
            <Route
              path={NavigationId.NEW_PRODUCT + "/*"}
              element={<NewProductRoute />}
            />
            <Route path={NavigationId.IMPORT} element={<ImportContacts />} />
            <Route
              path={NavigationId.INTEGRATIONS}
              element={<EventbriteIntegration />}
            />
            <Route path="/u/:username/*" element={<ProfileRoute />} />
            <Route path="/explore/*" element={<ExploreRoute />} />
            <Route path="/c/:communityId" element={<CommunityPage />} />
            <Route
              path="/public/markit-fundraising"
              element={<MarkitFundraisingScreen />}
            />
            <Route
              path="/admin/master-dashboard/*"
              element={<MasterDashboardScreen />}
            />
            <Route
              path={NavigationId.OAUTH_CONSENT}
              element={<OAuthConsentScreen />}
            />

            {/** Login Routes */}
            <Fragment>
              <Route path={NavigationId.SIGN_IN} element={<LoginWelcome />} />
              <Route
                path={NavigationId.ONBOARDING}
                element={<LoginOnboarding />}
              />
              <Route
                path={NavigationId.WELCOME}
                element={<WelcomeLandingPage />}
              />
            </Fragment>
            {/** Landing Page Routes */}
            <Fragment>
              <Route path="*" element={<DefaultLandingPage />} />
              <Route
                path={LandingPageNavigationId.PRICING}
                element={<PricingPage />}
              />
              <Route
                path={LandingPageNavigationId.PRESS_MEDIA}
                element={<PressMediaPage />}
              />
              <Route
                path={LandingPageNavigationId.TEXTING}
                element={<TextingProductPage />}
              />
              <Route
                path={LandingPageNavigationId.MONETIZATION}
                element={<MonetizationProductPage />}
              />
              {/** START Integration Redirect Success Routes */}
              <Route
                path={LandingPageNavigationId.STRIPE_SUCCESSFUL}
                element={<StripeSuccessfulPage />}
              />
              <Route
                path={LandingPageNavigationId.INSTAGRAM_CONNECTION}
                element={<InstagramConnectionPage />}
              />
              <Route
                path={LandingPageNavigationId.FACEBOOK_CONNECTION}
                element={<FacebookConnectionPage />}
              />
              <Route
                path={LandingPageNavigationId.GOOGLE_CALENDAR_CONNECTION}
                element={<GoogleCalendarConnectionPage />}
              />
              {/** END Integration Redirect Success Routes */}
            </Fragment>
          </Routes>
        </ScrollToTop>
        <ConfirmActionPopup />
        <MarkitPlusModal />
        <AccountMismatchModal />
        <UpdateWebAppBanner />
        <OnboardingTourStepIndicator />
      </>
    );
  }
};

export default App;
